import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Receipt,
} from "lucide-react";
import { useGetList } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import {
  PAYMENT_STATUSES,
  SUBSCRIPTION_KINDS,
  SUBSCRIPTION_STATUSES,
  formatEuro,
  formatLongDate,
  labelOf,
} from "../gulaConstants";
import { fullName, type GulaClient } from "../gulaTypes";
import { buildMoneyStats, monthlyAmount } from "./moneyStats";
import { MoneyTabs } from "./MoneyTabs";
import { RevenueChart } from "./RevenueChart";
import { useProfitRows } from "./useMoneyData";

const PAYMENT_KIND_LABEL: Record<string, string> = {
  equipment: "Equipo",
  rental: "Alquiler",
  maintenance: "Mantenimiento",
  installments: "Plazo",
  other: "Otro",
};

const Tile = ({
  label,
  value,
  hint,
  className,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  className?: string;
}) => (
  <div className={cn("rounded-lg border bg-card p-4", className)}>
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    {hint ? (
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    ) : null}
  </div>
);

const Change = ({ pct }: { pct: number | null }) => {
  if (pct == null) return <span>sin datos del mes pasado</span>;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5">
      <Icon
        className={cn(
          "h-3.5 w-3.5",
          up ? "text-emerald-600 dark:text-emerald-400" : "text-destructive",
        )}
        aria-hidden
      />
      {up ? "+" : ""}
      {pct}% que a estas alturas del mes pasado
    </span>
  );
};

/** Money panel: what Stripe has collected, recurring income and portfolio value. */
export const MoneyPage = () => {
  const { rows, payments, subscriptions, settings, isPending } =
    useProfitRows();
  const { data: clients = [] } = useGetList<GulaClient>(
    "gula_clients_summary",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const clientByContact = new Map(
    clients.map((client) => [String(client.contact_id), client]),
  );
  const clientLink = (contactId: unknown) => {
    const client = clientByContact.get(String(contactId));
    return client
      ? { name: fullName(client), to: `/clientes?cliente=${client.id}` }
      : { name: "Cliente", to: "/clientes" };
  };

  const now = new Date();
  const stats = buildMoneyStats({ payments, subscriptions, now });
  const profitToDate = rows.reduce(
    (sum, row) => sum + row.result.profitToDate,
    0,
  );
  const reviewed = rows.filter((row) => row.record).length;
  const monthlyProfit = rows.reduce(
    (sum, row) => sum + row.result.recurringProfitMonth,
    0,
  );

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Dinero</h1>
          <p className="text-sm text-muted-foreground">
            Cobros de Stripe con IVA incluido · se actualiza solo con cada pago
          </p>
        </div>
        <MoneyTabs />
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando cobros…</p>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-lg border bg-card p-5 md:col-span-2">
              <div className="text-sm text-muted-foreground">
                Hoy habéis cobrado
              </div>
              <div className="mt-1 text-4xl font-semibold tabular-nums sm:text-5xl">
                {formatEuro(stats.today)}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {stats.todayCount
                  ? `${stats.todayCount} ${stats.todayCount === 1 ? "cobro" : "cobros"} hoy · ${formatEuro(stats.week)} esta semana`
                  : `Todavía ningún cobro hoy · ${formatEuro(stats.week)} esta semana`}
              </div>
            </div>
            <Tile
              label="Este mes"
              value={formatEuro(stats.month)}
              hint={<Change pct={stats.monthChangePct} />}
            />
            <Tile
              label={`En ${now.getFullYear()}`}
              value={formatEuro(stats.year)}
              hint={`${formatEuro(stats.allTime)} desde el principio`}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="Entra cada mes (cuotas)"
              value={formatEuro(stats.mrr)}
              hint={`Alquiler ${formatEuro(stats.mrrByKind.rental)} · Mantenimiento ${formatEuro(stats.mrrByKind.maintenance)}`}
            />
            <Tile
              label="Cuotas en 12 meses"
              value={formatEuro(stats.mrr * 12)}
              hint={`${stats.activeRecurring} cuotas activas${stats.installmentsPending ? ` · ${stats.installmentsPending} pago a plazos en curso` : ""}`}
            />
            <Tile
              label="Valor medio de un cliente"
              value={formatEuro(stats.averageClientValue)}
              hint={`${stats.payingClients} clientes han pagado algo`}
            />
            <Link
              to="/dinero/beneficio"
              className="rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50"
            >
              <div className="text-sm text-muted-foreground">
                Beneficio a hoy (ventas)
              </div>
              <div
                className={cn(
                  "mt-1 text-2xl font-semibold tabular-nums",
                  profitToDate < 0 && "text-destructive",
                )}
              >
                {formatEuro(Math.round(profitToDate * 100) / 100)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {settings && !Number(settings.equipment_cost)
                  ? "Faltan los costes por defecto: aún no descuenta equipo, instalador ni envío"
                  : `${reviewed} de ${rows.length} ventas con costes revisados · +${formatEuro(Math.round(monthlyProfit * 100) / 100)} cada mes`}
              </div>
            </Link>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <Card className="gap-3 p-4 lg:col-span-2">
              <div>
                <h2 className="font-semibold">Cobros por mes</h2>
                <p className="text-xs text-muted-foreground">
                  Últimos 12 meses, IVA incluido
                </p>
              </div>
              <RevenueChart months={stats.months} />
            </Card>

            <Card className="gap-3 p-4">
              <h2 className="font-semibold">Mejores clientes</h2>
              <ol className="flex flex-col gap-2 text-sm">
                {stats.topClients.map((client, index) => {
                  const link = clientLink(client.contactId);
                  return (
                    <li
                      key={String(client.contactId)}
                      className="flex items-center justify-between gap-2"
                    >
                      <Link
                        to={link.to}
                        className="min-w-0 truncate hover:underline"
                      >
                        <span className="mr-2 text-muted-foreground">
                          {index + 1}.
                        </span>
                        {link.name}
                      </Link>
                      <span className="tabular-nums">
                        {formatEuro(client.total)}
                      </span>
                    </li>
                  );
                })}
              </ol>

              {stats.atRisk.length ? (
                <div className="mt-2 flex flex-col gap-2 border-t pt-3">
                  <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                    <AlertTriangle
                      className="h-4 w-4 text-destructive"
                      aria-hidden
                    />
                    Cuotas con impago
                  </h3>
                  {stats.atRisk.map((subscription) => {
                    const link = clientLink(subscription.contact_id);
                    return (
                      <Link
                        key={String(subscription.id)}
                        to={link.to}
                        className="flex justify-between gap-2 text-sm hover:underline"
                      >
                        <span className="truncate">{link.name}</span>
                        <span className="whitespace-nowrap text-muted-foreground">
                          {labelOf(SUBSCRIPTION_KINDS, subscription.kind)} ·{" "}
                          {formatEuro(monthlyAmount(subscription))}/mes ·{" "}
                          {labelOf(SUBSCRIPTION_STATUSES, subscription.status)}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </Card>
          </div>

          <Card className="gap-3 p-4">
            <h2 className="flex items-center gap-2 font-semibold">
              <Receipt className="h-4 w-4 text-muted-foreground" aria-hidden />
              Últimos cobros
            </h2>
            <div className="flex flex-col divide-y text-sm">
              {stats.recent.map((payment) => {
                const link = clientLink(payment.contact_id);
                return (
                  <div
                    key={String(payment.id)}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <div className="flex min-w-0 flex-col">
                      <Link to={link.to} className="truncate hover:underline">
                        {link.name}
                      </Link>
                      <span className="truncate text-xs text-muted-foreground">
                        {PAYMENT_KIND_LABEL[payment.kind] ?? payment.kind} ·{" "}
                        {formatLongDate(payment.paid_at)}
                        {payment.status !== "paid"
                          ? ` · ${labelOf(PAYMENT_STATUSES, payment.status)}`
                          : ""}
                      </span>
                    </div>
                    <span className="font-medium tabular-nums">
                      {formatEuro(Number(payment.amount))}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

MoneyPage.path = "/dinero";
